/**
 * Seed data for the 2026 Woodsmen at Bandon Dunes.
 *
 * IMPORTANT — the hole data below is TRANSCRIBED, not verified against the
 * physical card. Every course ships `verified: false`, and the app refuses to
 * hide that state anywhere it matters.
 *
 * These are no longer placeholders. Pars and stroke indexes are corroborated
 * across three to four independent published cards per course, and the old
 * par-72 confusion on Pacific Dunes is resolved (36 out, 35 in, par 71).
 * Ratings and slopes are the field that is still soft; see each course's
 * `provenance` for exactly where and by how much the sources disagree.
 *
 * Stroke index is the field that must be right — it decides which holes each
 * player pops on, and unlike a wrong par, a transposed stroke index is
 * invisible and wrong all week. Confirm against the real card before the first
 * tee shot; `provenance` names the cells worth photographing.
 */

import { DEFAULT_QUOTA_TABLE } from '../engine/types'
import type { Course, Match } from '../engine/types'
import { cardHoles, tees, type Seed } from './card'
import { AVONDALE } from './courses/avondale'
import { CIRCLING_RAVEN } from './courses/circling-raven'
import { COEUR_DALENE_RESORT } from './courses/coeur-dalene-resort'
import { GOZZER_RANCH } from './courses/gozzer-ranch'

/*
 * Cards transcribed 2026-07-28 from public scorecard databases, because the
 * resort's own site refuses automated requests, then independently re-checked
 * 2026-07-29 against two further sources — BlueGolf's course database and
 * golfify — which reach cells the first pass could not.
 *
 * NONE of this is `verified`. That flag means a human matched it to the
 * physical card, and nothing here clears that bar. But the second pass changed
 * the shape of the doubt considerably: all 90 stroke indexes are now confirmed
 * by at least two independent sources, every yardage cell is filled, and every
 * tee total but one equals the column printed above it.
 *
 * Rule applied on the second pass: a number only changed where two independent
 * sources agreed exactly against the existing value. Where sources merely
 * disagreed, the existing value stayed and `provenance` records the spread —
 * nothing was averaged, and no yardage was invented. Ratings are now the
 * weakest field on every card, and stroke index the strongest, which is the
 * right way round: a wrong rating shifts everyone's course handicap by the
 * same amount, a wrong stroke index silently misallocates pops all week.
 *
 * Where sources still disagree, `provenance` says so and names the holes, so
 * whoever walks the pro shop with a phone knows which cells to photograph.
 */
const SEEDS: Seed[] = [
  {
    id: 'bandon-dunes',
    name: 'Bandon Dunes',
    par: 72,
    pars: [4, 3, 5, 4, 4, 3, 4, 4, 5, 4, 4, 3, 5, 4, 3, 4, 4, 5],
    si: [13, 15, 3, 5, 1, 17, 7, 11, 9, 8, 2, 18, 6, 16, 14, 10, 12, 4],
    yards: [
      [386, 189, 543, 410, 428, 161, 383, 359, 558, 362, 384, 199, 553, 359, 163, 363, 389, 543],
      [352, 155, 489, 362, 400, 153, 372, 342, 520, 339, 351, 153, 537, 332, 131, 345, 375, 513],
      [332, 136, 467, 340, 374, 126, 332, 321, 510, 302, 315, 129, 498, 320, 113, 301, 329, 471],
      [293, 117, 351, 308, 321, 111, 317, 290, 464, 251, 284, 98, 447, 288, 102, 250, 324, 424],
      [223, 117, 318, 228, 260, 111, 232, 220, 335, 200, 228, 98, 344, 205, 80, 210, 244, 333],
    ],
    tees: tees([
      ['black', 'Black', 73.5, 143, 6732],
      ['green', 'Green', 71.1, 133, 6221],
      ['gold', 'Gold', 69.1, 124, 5716],
    ]),
    verified: true,
    provenance:
      'VERIFIED against a photograph of the physical card, 2026-07-29. Par ' +
      'and stroke index were already correct. Corrected here: the Black, ' +
      'Green and Gold ratings (73.5/143, 71.1/133, 69.1/124 against the ' +
      '74.1/71.6/69.2 the databases gave), and the fourth tee, which was a ' +
      'phantom - there is no Bronze on this card. The real card carries Royal ' +
      'Blue (61.6/105, 3986) and Orange (65.5/114, 5040), and both are now ' +
      'here with every yardage. The two cells the old provenance flagged as ' +
      'doubtful, holes 2 and 3 of that column, were indeed wrong: 117 and ' +
      '351, not 130 and 423.' +
      ' RECHECKED 2026-08-12 against fresh photographs of all five physical cards (shipped in public/cards/): every par, stroke index, Black/Green/Gold yardage and rating matched, by two independent readings - a human transcription and a vision-model read of the same images, cross-checked cell by cell in cards-as-photographed.ts.',
  },
  {
    id: 'pacific-dunes',
    name: 'Pacific Dunes',
    par: 71,
    pars: [4, 4, 5, 4, 3, 4, 4, 4, 4, 3, 3, 5, 4, 3, 5, 4, 3, 5],
    si: [9, 11, 7, 3, 17, 13, 1, 5, 15, 14, 18, 6, 2, 16, 10, 12, 8, 4],
    yards: [
      [370, 368, 499, 463, 199, 316, 464, 400, 406, 206, 148, 529, 444, 145, 539, 338, 208, 591],
      [304, 335, 476, 449, 181, 288, 436, 369, 379, 163, 131, 507, 390, 128, 504, 338, 189, 575],
      [287, 335, 476, 410, 163, 288, 377, 349, 356, 149, 114, 476, 371, 119, 492, 301, 164, 548],
      [253, 275, 452, 369, 133, 267, 314, 296, 268, 129, 96, 449, 336, 100, 452, 292, 128, 479],
      [200, 180, 309, 320, 115, 167, 239, 240, 262, 128, 96, 330, 250, 100, 320, 200, 118, 346],
    ],
    tees: tees([
      ['black', 'Black', 73.2, 143, 6633],
      ['green', 'Green', 70.8, 135, 6142],
      ['gold', 'Gold', 68.9, 131, 5775],
    ]),
    verified: true,
    provenance:
      'VERIFIED against a photograph of the physical card, 2026-07-29. Par ' +
      '(36 out, 35 in, 71) and all eighteen stroke indexes were already ' +
      'correct. Corrected: the Green and Gold ratings (70.8/135 and ' +
      '68.9/131), and the phantom Bronze tee replaced by the real Royal Blue ' +
      '(61.5/113) and Orange (65.8/126).' +
      ' RECHECKED 2026-08-12 against fresh photographs of all five physical cards (shipped in public/cards/): every par, stroke index, Black/Green/Gold yardage and rating matched, by two independent readings - a human transcription and a vision-model read of the same images, cross-checked cell by cell in cards-as-photographed.ts.',
  },
  {
    id: 'bandon-trails',
    name: 'Bandon Trails',
    par: 71,
    pars: [4, 3, 5, 4, 3, 4, 4, 4, 5, 4, 4, 3, 4, 4, 4, 5, 3, 4],
    si: [13, 17, 3, 5, 15, 9, 7, 11, 1, 10, 4, 18, 12, 14, 8, 2, 16, 6],
    yards: [
      [392, 214, 549, 408, 133, 395, 440, 321, 567, 418, 445, 242, 401, 325, 406, 530, 182, 420],
      [356, 166, 532, 365, 124, 359, 406, 299, 522, 393, 429, 235, 374, 306, 367, 494, 159, 363],
      [288, 142, 513, 334, 114, 315, 373, 283, 497, 369, 398, 220, 354, 292, 323, 466, 138, 332],
      [212, 84, 455, 315, 104, 293, 351, 243, 456, 326, 382, 192, 308, 251, 296, 437, 128, 264],
      [212, 84, 356, 200, 104, 204, 264, 243, 353, 255, 279, 153, 243, 174, 239, 260, 92, 222],
    ],
    tees: tees([
      ['black', 'Black', 74.9, 136, 6788],
      ['green', 'Green', 72.0, 137, 6249],
      ['gold', 'Gold', 69.6, 132, 5751],
    ]),
    verified: true,
    provenance:
      'CORRECTED 2026-08-10, and this card has now been wrong in both directions. ' +
      'A 2026-07-29 pass concluded this card and Old Macdonald had been transposed ' +
      'and swapped them. That was the error: Scott confirmed from the physical ' +
      'cards that Old Macdonald carries its par 5s on 6, 15 and 17, which is what ' +
      'this file had filed under Bandon Trails. Four public databases had said so ' +
      'and were dismissed. The bodies are swapped back. Bandon Trails is par 71, ' +
      '36 out and 35 in, par 5s on 3, 9 and 16, off 74.9/136 at 6788 from the Black. ' +
      'Both arrangements sum and permute correctly, which is exactly why no self- ' +
      'check and no test could tell them apart - only the card can.' +
      ' RECHECKED 2026-08-12 against fresh photographs of all five physical cards (shipped in public/cards/): every par, stroke index, Black/Green/Gold yardage and rating matched, by two independent readings - a human transcription and a vision-model read of the same images, cross-checked cell by cell in cards-as-photographed.ts.',
  },
  {
    id: 'old-macdonald',
    name: 'Old Macdonald',
    par: 71,
    pars: [4, 3, 4, 4, 3, 5, 4, 3, 4, 4, 4, 3, 4, 4, 5, 4, 5, 4],
    si: [11, 15, 9, 1, 17, 3, 5, 13, 7, 6, 4, 16, 18, 14, 12, 2, 10, 8],
    yards: [
      [341, 181, 375, 504, 160, 555, 363, 181, 416, 465, 445, 237, 346, 370, 535, 455, 546, 469],
      [304, 162, 345, 472, 134, 520, 345, 170, 352, 440, 399, 205, 319, 297, 482, 433, 515, 426],
      [275, 139, 312, 441, 121, 467, 322, 152, 343, 383, 390, 164, 253, 254, 424, 408, 397, 413],
      [242, 106, 244, 391, 100, 401, 311, 130, 284, 369, 344, 149, 247, 238, 340, 326, 391, 372],
      [213, 102, 217, 283, 100, 354, 195, 121, 231, 278, 250, 91, 220, 184, 313, 270, 343, 275],
    ],
    tees: tees([
      ['black', 'Black', 74.4, 134, 6944],
      ['green', 'Green', 71.4, 127, 6320],
      ['gold', 'Gold', 67.8, 117, 5658],
    ]),
    verified: true,
    provenance:
      'CORRECTED 2026-08-10 - see the Bandon Trails note. These two cards were ' +
      'swapped on 2026-07-29 on the strength of a photograph, and the swap was ' +
      'itself the error. Old Macdonald is par 71, 34 out and 37 in, par 5s on 6, ' +
      '15 and 17, off 74.4/134 at 6944 from the Black - confirmed by Scott from ' +
      'the cards, and agreeing with every public database.' +
      ' RECHECKED 2026-08-12 against fresh photographs of all five physical cards (shipped in public/cards/): every par, stroke index, Black/Green/Gold yardage and rating matched, by two independent readings - a human transcription and a vision-model read of the same images, cross-checked cell by cell in cards-as-photographed.ts.',
  },
  {
    id: 'sheep-ranch',
    name: 'Sheep Ranch',
    par: 72,
    pars: [5, 4, 3, 4, 3, 4, 3, 4, 4, 4, 5, 4, 5, 4, 4, 3, 4, 5],
    si: [5, 13, 17, 3, 11, 1, 15, 7, 9, 6, 4, 2, 10, 8, 14, 16, 12, 18],
    yards: [
      [549, 318, 120, 474, 198, 460, 155, 429, 399, 390, 529, 440, 510, 403, 321, 151, 326, 464],
      [517, 303, 113, 443, 166, 431, 138, 407, 386, 375, 506, 414, 485, 377, 303, 131, 314, 436],
      [491, 282, 101, 415, 139, 401, 110, 382, 361, 356, 463, 390, 464, 354, 279, 120, 297, 405],
      [422, 263, 94, 379, 130, 318, 99, 337, 339, 316, 411, 318, 377, 325, 260, 102, 266, 388],
      [307, 231, 75, 259, 91, 246, 88, 264, 239, 229, 296, 231, 319, 237, 210, 90, 228, 303],
    ],
    tees: tees([
      ['black', 'Black', 71.9, 121, 6636],
      ['green', 'Green', 70.0, 116, 6245],
      ['gold', 'Gold', 67.9, 109, 5810],
    ]),
    verified: true,
    provenance:
      'VERIFIED against a photograph of the physical card, 2026-07-29. The ' +
      'cleanest of the five: par (34 out, 38 in, 72), all eighteen stroke ' +
      'indexes and the Black, Green and Gold ratings already matched the card ' +
      'exactly. Only the fourth tee changed - the phantom Bronze carried the ' +
      'Orange numbers under the wrong name, and the real Royal Blue (61.0/97) ' +
      'was missing entirely.' +
      ' RECHECKED 2026-08-12 against fresh photographs of all five physical cards (shipped in public/cards/): every par, stroke index, Black/Green/Gold yardage and rating matched, by two independent readings - a human transcription and a vision-model read of the same images, cross-checked cell by cell in cards-as-photographed.ts.',
  },
]

const toCourse = (s: Seed): Course => ({
  id: s.id,
  name: s.name,
  par: s.par,
  /*
   * A tee keeps its OWN par when it states one, and inherits the course's only
   * when it does not. This line used to force the course par onto every tee,
   * which is where "Royal Blue, par 72" came from — see `tees` in card.ts.
   */
  tees: s.tees.map((t) => ({ ...t, par: t.par || s.par })),
  holes: cardHoles(s.pars, s.si, s.yards),
  verified: s.verified ?? false,
  notes:
    (s.verified
      ? 'Checked against a photograph of the physical card. '
      : 'Transcribed from public scorecard databases, NOT from the physical ' +
        'card. Confirm par and stroke index before scoring — stroke index ' +
        'decides every pop, and a transposed one is invisible and wrong all ' +
        'week. ') + s.provenance,
})

export const BANDON_COURSES: Course[] = SEEDS.map(toCourse)

/**
 * The Idaho courses, from the years before Bandon.
 *
 * 2025 played Gozzer Ranch, Circling Raven and the Coeur d'Alene resort course;
 * 2024 played Coeur d'Alene, Gozzer and Avondale. They are here so a past year
 * can be reconstructed and scored rather than only remembered — the app is
 * multi-tournament, and a tournament without its courses cannot compute a
 * single pop.
 *
 * Avondale is the best-attested card in the app: it was transcribed from a
 * photograph of the group's own 2024 scorecard and then confirmed hole for hole
 * against three databases. Everything else here is public-database work with
 * its doubts written into `provenance`.
 */
export const IDAHO_COURSES: Course[] = [
  GOZZER_RANCH,
  CIRCLING_RAVEN,
  COEUR_DALENE_RESORT,
  AVONDALE,
].map(toCourse)

/** Every card the app knows, for the picker and for the invariant tests. */
export const ALL_COURSES: Course[] = [...BANDON_COURSES, ...IDAHO_COURSES]

export const ALL_SEEDS: Seed[] = [
  ...SEEDS,
  GOZZER_RANCH,
  CIRCLING_RAVEN,
  COEUR_DALENE_RESORT,
  AVONDALE,
]

/** The 2025 field, as a starting roster the admin can edit. */
export const ROSTER: { id: string; displayName: string; fullName: string }[] = [
  { id: 'tyler', displayName: 'Tyler', fullName: 'Tyler Godfrey' },
  { id: 'danny', displayName: 'Danny', fullName: 'Danny' },
  { id: 'nixon', displayName: 'Nixon', fullName: 'Nixon' },
  { id: 'poulton', displayName: 'Poulton', fullName: 'Poulton' },
  { id: 'wittwer', displayName: 'Wittwer', fullName: 'Wittwer' },
  { id: 'springer', displayName: 'Springer', fullName: 'Springer' },
  { id: 'thomas', displayName: 'Thomas', fullName: 'Thomas' },
  { id: 'phil', displayName: 'Phil', fullName: 'Phil' },
  { id: 'blackham', displayName: 'Blackham', fullName: 'Blackham' },
  { id: 'eric', displayName: 'Eric', fullName: 'Eric' },
  { id: 'scottp', displayName: 'Scott P.', fullName: 'Scott P.' },
  { id: 'cramer', displayName: 'Cramer', fullName: 'Scott Cramer' },
]

/** Payout ladders, transcribed from the sheet — 2025's, revised by Shawn's 2026. */
export const PAYOUTS = {
  headToHead9: {
    win: { points: 4, cash: 100 },
    halve: { points: 2, cash: 50 },
    loss: { points: 0, cash: 0 },
  },
  /**
   * Four-ball is on the SAME ladder as every other head-to-head nine.
   *
   * This used to carry three points, and the reasoning was sound at the time:
   * the 2025 sheet really did pay four-ball less than singles, and pointing it
   * at `headToHead9` would have overpaid the seeding by a point a man.
   *
   * Shawn's 2026 sheet revised it, and says so on all three of the rows this
   * week plays — "Bandon Dunes — 2-Man Best Ball ($100/match/winner + 4 pts)",
   * "Trails — 2-Man Best Ball ($100/match/winner + 4 pts)", and Match 5's
   * "$100 per match/winner + 4 points to winner". Scott confirmed it. The live
   * document was already on four; this constant was the stale copy, and it is
   * reachable — re-picking Team Four-ball in the format dropdown rewrites a
   * match's config wholesale, and an ad-hoc counting four-ball is built from
   * here too.
   *
   * The halve is half the win in cash and points both, which is what keeps the
   * pot self-financing: two men at $100 on a win against four at $50 on a
   * halve. No fractional points anywhere in the seeding now.
   */
  fourBall9: {
    win: { points: 4, cash: 100 },
    halve: { points: 2, cash: 50 },
    loss: { points: 0, cash: 0 },
  },
  quotaGroup: [
    { from: 1, to: 1, points: 5, cash: 150 },
    { from: 2, to: 2, points: 2, cash: 100 },
    { from: 3, to: 3, points: 1, cash: 0 },
  ],
  /*
   * The 2025 sheet's own ladder, restored from a photograph of it: 6/5/4 for
   * the money places, then 2 for 4th–6th and 1 for 7th–9th. The sheet says
   * nothing about 10th–12th, and the silence is the rule — the bottom three
   * of a nine earn nothing. The app had been paying 3/2/1 down the tail,
   * which handed every man a point for turning up.
   */
  quotaField: [
    { from: 1, to: 1, points: 6, cash: 300 },
    { from: 2, to: 2, points: 5, cash: 200 },
    { from: 3, to: 3, points: 4, cash: 100 },
    { from: 4, to: 6, points: 2, cash: 0 },
    { from: 7, to: 9, points: 1, cash: 0 },
  ],
  championship: [
    { name: 'Flight 1', fromSeed: 1, toSeed: 4, payouts: [
      { from: 1, to: 1, points: 0, cash: 1500 },
      { from: 2, to: 2, points: 0, cash: 750 },
      { from: 3, to: 3, points: 0, cash: 300 },
      { from: 4, to: 4, points: 0, cash: 250 },
    ] },
    { name: 'Flight 2', fromSeed: 5, toSeed: 8, payouts: [
      { from: 1, to: 1, points: 0, cash: 750 },
    ] },
    { name: 'Flight 3', fromSeed: 9, toSeed: 12, payouts: [
      { from: 1, to: 1, points: 0, cash: 500 },
    ] },
  ],
}

/** The seven format templates the admin picks from when building a round. */
export const FORMAT_PRESETS = [
  {
    id: 'individualMatchPlay',
    label: 'Individual Match Play',
    blurb: 'Two 1v1 matches inside each foursome. Low net wins the hole, ties halved.',
    holes: 9,
  },
  {
    id: 'teamHighLow',
    label: 'Team High / Low',
    blurb: '2v2. A point a hole for the low ball, a point for the high.',
    holes: 9,
  },
  {
    id: 'teamCombinedNet',
    label: 'Team Combined Net',
    blurb: '2v2. Both net scores added; the lower team total wins the hole.',
    holes: 9,
  },
  {
    id: 'teamFourBall',
    label: 'Team Four-ball (Best Ball)',
    blurb: '2v2. Play your own ball; the better net of the two is the team score.',
    holes: 9,
  },
  {
    id: 'quotaGroup',
    label: 'Quota — vs your group',
    blurb: 'Start at a handicap-based target. Ranked against your three groupmates.',
    holes: 18,
  },
  {
    id: 'quotaField',
    label: 'Quota — vs the field',
    blurb: 'Same table, ranked against all twelve.',
    holes: 9,
  },
  {
    id: 'strokeNet',
    label: 'Championship — Net Stroke Play',
    blurb: 'Flighted by seeding. Start at your handicap under par; no pop holes.',
    holes: 18,
  },
] as const

export type FormatPresetId = (typeof FORMAT_PRESETS)[number]['id']

/**
 * The format config a preset produces.
 *
 * Lived inside the Setup screen until an ad-hoc round needed to build one too,
 * and two copies of a payout ladder is two ladders that can disagree about what
 * a four-ball win pays. It belongs beside the presets and the payouts it is
 * assembled from.
 */
export function configFor(id: FormatPresetId): Match['config'] {
  switch (id) {
    case 'individualMatchPlay':
      return {
        kind: 'individualMatchPlay',
        netBasis: 'relativeToLowInUnit',
        payout: PAYOUTS.headToHead9,
        closeout: true,
      }
    case 'teamHighLow':
      return {
        kind: 'teamHighLow',
        netBasis: 'relativeToLowInGroup',
        pointsPerHole: { low: 1, high: 1 },
        tieValue: 0.5,
        payout: PAYOUTS.headToHead9,
      }
    case 'teamCombinedNet':
      return {
        kind: 'teamCombinedNet',
        netBasis: 'relativeToLowInGroup',
        payout: PAYOUTS.headToHead9,
        closeout: true,
      }
    case 'teamFourBall':
      // Same frame as the other 2v2s: one low man in the whole foursome, so
      // both sides pop off the same reference. Allocating per side would let a
      // team's own low handicap decide how many strokes it gave away.
      return {
        kind: 'teamFourBall',
        netBasis: 'relativeToLowInGroup',
        payout: PAYOUTS.fourBall9,
        closeout: true,
      }
    case 'quotaGroup':
      return {
        kind: 'quota',
        rankingScope: 'group',
        table: DEFAULT_QUOTA_TABLE,
        quotaFormula: { pointsPerPar: 2, subtractHandicap: true },
        scoreBasis: 'gross',
        payouts: PAYOUTS.quotaGroup,
      }
    case 'quotaField':
      return {
        kind: 'quota',
        rankingScope: 'field',
        table: DEFAULT_QUOTA_TABLE,
        quotaFormula: { pointsPerPar: 2, subtractHandicap: true },
        scoreBasis: 'gross',
        payouts: PAYOUTS.quotaField,
      }
    case 'strokeNet':
      return {
        kind: 'strokeNet',
        netBasis: 'full',
        maxOverParNet: 3,
        // Triple bogey max on a hole a man finishes — Shawn's 2026 sheet, and
        // Scott's ruling that it caps the GROSS while the allowance stays a
        // starting figure.
        maxOverParGross: 3,
        flighted: true,
        flights: PAYOUTS.championship,
      }
  }
}

/**
 * A card with no contest on it — the default for a round added for the fun of
 * it.
 *
 * Net stroke play, unflighted, paying nothing, which is precisely what a
 * scorecard IS: gross, net off your full handicap, and a total. `flighted`
 * false is what keeps it from being mistaken for the championship, which is
 * identified by the flighting rather than by the kind — see `computeAll`.
 *
 * No blow-up cap. A cap is a competition rule and there is no competition here;
 * a man who makes nine on the 7th has made nine.
 */
export const CARD_CONFIG: Match['config'] = {
  kind: 'strokeNet',
  netBasis: 'full',
  maxOverParNet: null,
  // No competition, so no ceiling: a man who makes nine has made nine.
  maxOverParGross: null,
  flighted: false,
  flights: [],
}
